import React, { useState, useEffect, useRef } from 'react';
import {
  Box, VStack, HStack, Heading, Text, Button, Radio, RadioGroup,
  Card, CardBody, useToast, Progress, Alert, AlertIcon,
  Badge, Icon, Divider, Stat, StatLabel, StatNumber,
  StatGroup, useDisclosure
} from '@chakra-ui/react';
import { 
  MdCloudUpload, MdEmail, MdCheckCircle, MdError, 
  MdWarning, MdDownload, MdUploadFile, MdRefresh 
} from 'react-icons/md';
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL
  : process.env.REACT_APP_LOCAL_API_URL;

const getToken = () => localStorage.getItem('sul_investimentos_token');

const OABEmailImport = () => {
  const [selectedState, setSelectedState] = useState('');
  const [availableStates, setAvailableStates] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const [progressInterval, setProgressInterval] = useState(null);
  const [stateStatistics, setStateStatistics] = useState(null);
  const [loadingStatistics, setLoadingStatistics] = useState(false);
  
  const fileInputRef = useRef(null);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  // Fetch available states on mount
  useEffect(() => {
    fetchAvailableStates();
  }, []);
  
  // Fetch statistics when state changes
  useEffect(() => {
    if (selectedState) {
      fetchStateStatistics(selectedState);
    } else {
      setStateStatistics(null);
    }
  }, [selectedState]);
  
  const fetchAvailableStates = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/oab/states`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch states');
      }
      
      const data = await response.json();
      
      if (data.success) {
        setAvailableStates(data.states);
        // Set default state if available
        if (data.states.length > 0) {
          setSelectedState(data.states[0].code);
        }
      }
    } catch (error) {
      toast({
        title: 'Erro ao carregar estados',
        description: error.message || 'Erro ao buscar estados disponíveis',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };
  
  const fetchStateStatistics = async (state) => {
    setLoadingStatistics(true);
    try {
      const response = await fetch(`${API_BASE_URL}/oab/statistics/${state}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch statistics');
      }
      
      const data = await response.json();
      
      if (data.success) {
        setStateStatistics(data.statistics);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
      toast({
        title: 'Erro ao carregar estatísticas',
        description: error.message || 'Erro ao buscar estatísticas do estado',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingStatistics(false);
    }
  };
  
  // Handle drag and drop
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelection(e.dataTransfer.files[0]);
    }
  };
  
  const handleFileSelection = (file) => {
    // Validate file type
    const fileExt = file.name.toLowerCase();
    if (!fileExt.endsWith('.csv') && !fileExt.endsWith('.xlsx') && !fileExt.endsWith('.xls')) {
      toast({
        title: 'Arquivo inválido',
        description: 'Por favor, selecione um arquivo CSV ou Excel',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: 'Arquivo muito grande',
        description: 'O arquivo deve ter no máximo 10MB',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setSelectedFile(file);
    setImportResults(null);
  };
  
  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelection(e.target.files[0]);
    }
  };
  
  const handleImport = async () => {
    if (!selectedFile || !selectedState) {
      toast({
        title: 'Dados incompletos',
        description: 'Por favor, selecione um estado e um arquivo CSV ou Excel',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setImporting(true);
    
    const formData = new FormData();
    formData.append('csvFile', selectedFile);
    formData.append('state', selectedState);
    
    try {
      const response = await fetch(`${API_BASE_URL}/oab/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          // Don't set Content-Type for FormData, let the browser set it
        },
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setImportResults(data);
        
        // Start polling for progress if importId is returned
        if (data.importId) {
          pollProgress(data.importId);
        }
        
        toast({
          title: 'Importação concluída',
          description: `${data.summary.successfulUpdates} OABs atualizados com sucesso`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        throw new Error(data.message || 'Erro ao importar arquivo');
      }
    } catch (error) {
      console.error('Import error:', error);
      toast({
        title: 'Erro na importação',
        description: error.message || 'Erro ao importar arquivo',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setImporting(false);
    }
  };
  
  // Poll for import progress
  const pollProgress = async (importId) => {
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/oab/progress/${importId}`, {
          headers: {
            'Authorization': `Bearer ${getToken()}`,
          }
        });
      
        
        if (!response.ok) {
          clearInterval(interval);
          return;
        }
        
        const data = await response.json();
        
        if (data.success && data.progress) {
          setImportProgress(data.progress);
          
          // Stop polling when import is completed or failed
          if (data.progress.status === 'completed' || data.progress.status === 'failed') {
            clearInterval(interval);
            setImporting(false);
          }
        }
      } catch (error) {
        console.error('Error polling progress:', error);
        clearInterval(interval);
      }
    }, 2000); // Poll every 2 seconds
    
    setProgressInterval(interval);
  };
  
  const handleReset = () => {
    setSelectedFile(null);
    setImportResults(null);
    setImportProgress(null);
    
    // Clear any ongoing progress polling
    if (progressInterval) {
      clearInterval(progressInterval);
      setProgressInterval(null);
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  // Export OABs without emails
  const handleExportWithoutEmails = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/oab/export-without-emails/${selectedState}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erro ao exportar OABs sem email');
      }
      
      // Create blob from response
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `oabs_sem_email_${selectedState}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast({
        title: 'Exportação concluída',
        description: 'Arquivo CSV baixado com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
    } catch (error) {
      console.error('Error exporting OABs without emails:', error);
      toast({
        title: 'Erro ao exportar',
        description: error.message || 'Erro ao exportar OABs sem email',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };
  
  return (
    <Box p={{ base: 4, md: 8 }}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box>
          <Heading size="lg" mb={2}>
            <Icon as={MdEmail} mr={2} />
            Importar Emails para OABs
          </Heading>
          <Text color="gray.600">
            Importe dados de um arquivo CSV ou Excel para associar aos registros de OABs
          </Text>
        </Box>
        
        {/* State Selection */}
        <Card>
          <CardBody>
            <VStack align="stretch" spacing={4}>
              <Text fontWeight="bold">Selecione o Estado:</Text>
              <RadioGroup value={selectedState} onChange={setSelectedState}>
                <HStack spacing={6}>
                  {availableStates.map(state => (
                    <Radio key={state.code} value={state.code} colorScheme="purple">
                      {state.name} ({state.code})
                    </Radio>
                  ))}
                </HStack>
              </RadioGroup>
            </VStack>
          </CardBody>
        </Card>
        
        {/* State Statistics */}
        {selectedState && stateStatistics && (
          <Card bg="blue.50" borderColor="blue.200" borderWidth="1px">
            <CardBody>
              <VStack align="stretch" spacing={4}>
                <HStack justify="space-between">
                  <Heading size="sm">
                    Estatísticas de {availableStates.find(s => s.code === selectedState)?.name || selectedState}
                  </Heading>
                  <HStack>
                    {loadingStatistics && <Badge colorScheme="blue">Atualizando...</Badge>}
                    {stateStatistics && stateStatistics.oabsWithoutEmails > 0 && (
                      <Button
                        size="sm"
                        leftIcon={<MdDownload />}
                        onClick={handleExportWithoutEmails}
                        variant="solid"
                        colorScheme="orange"
                      >
                        Exportar OABs sem Email
                      </Button>
                    )}
                    <Button
                      size="sm"
                      leftIcon={<MdRefresh />}
                      onClick={() => fetchStateStatistics(selectedState)}
                      isLoading={loadingStatistics}
                      loadingText="Atualizando"
                      variant="ghost"
                      colorScheme="blue"
                    >
                      Atualizar
                    </Button>
                  </HStack>
                </HStack>
                
                <StatGroup>
                  <Stat>
                    <StatLabel>Total de OABs</StatLabel>
                    <StatNumber>{stateStatistics.totalOabs.toLocaleString('pt-BR')}</StatNumber>
                  </Stat>
                  
                  <Stat>
                    <StatLabel>Com Emails</StatLabel>
                    <StatNumber color="green.600">
                      {stateStatistics.oabsWithEmails.toLocaleString('pt-BR')}
                      <Text as="span" fontSize="sm" color="green.500" ml={2}>
                        ({stateStatistics.emailCoverage}%)
                      </Text>
                    </StatNumber>
                  </Stat>
                  
                  <Stat>
                    <StatLabel>Sem Emails</StatLabel>
                    <StatNumber color="orange.600">
                      {stateStatistics.oabsWithoutEmails.toLocaleString('pt-BR')}
                    </StatNumber>
                  </Stat>
                  
                  <Stat>
                    <StatLabel>Com Telefones</StatLabel>
                    <StatNumber color="purple.600">
                      {stateStatistics.oabsWithPhones.toLocaleString('pt-BR')}
                      <Text as="span" fontSize="sm" color="purple.500" ml={2}>
                        ({stateStatistics.phoneCoverage}%)
                      </Text>
                    </StatNumber>
                  </Stat>
                </StatGroup>
                
                <Divider />
                
                <HStack spacing={6} fontSize="sm">
                  <HStack>
                    <Icon as={MdCheckCircle} color="teal.500" />
                    <Text>
                      <Text as="span" fontWeight="bold">{stateStatistics.cpfCoverage}%</Text> com CPF
                    </Text>
                  </HStack>
                  <HStack>
                    <Icon as={MdCheckCircle} color="cyan.500" />
                    <Text>
                      <Text as="span" fontWeight="bold">{stateStatistics.cidadeCoverage}%</Text> com Cidade
                    </Text>
                  </HStack>
                </HStack>
                
                {stateStatistics.oabsWithoutEmails > 0 && (
                  <Alert status="info" size="sm">
                    <AlertIcon />
                    <Text fontSize="sm">
                      {stateStatistics.oabsWithoutEmails.toLocaleString('pt-BR')} OABs podem receber emails através da importação
                    </Text>
                  </Alert>
                )}
              </VStack>
            </CardBody>
          </Card>
        )}
        
        {/* File Upload */}
        <Card>
          <CardBody>
            <VStack spacing={4}>
              <Box
                w="100%"
                h="200px"
                border="2px dashed"
                borderColor={dragActive ? "purple.400" : "gray.300"}
                borderRadius="lg"
                bg={dragActive ? "purple.50" : "gray.50"}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                cursor="pointer"
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                transition="all 0.2s"
                _hover={{ borderColor: "purple.400", bg: "purple.50" }}
              >
                <Icon as={MdCloudUpload} fontSize="48px" color="purple.500" mb={2} />
                <Text fontSize="lg" fontWeight="medium" mb={1}>
                  {selectedFile ? selectedFile.name : 'Arraste o arquivo CSV ou Excel aqui'}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  ou clique para selecionar
                </Text>
                {!selectedFile && (
                  <Text fontSize="xs" color="gray.400" mt={2}>
                    Colunas suportadas: INSCRICAO, EMAIL, Email1-3, CEl1-6, CPF, CIDADE, UF
                  </Text>
                )}
              </Box>
              
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
              />
              
              {selectedFile && (
                <Alert status="info">
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="bold">Arquivo selecionado:</Text>
                    <Text>{selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)</Text>
                  </Box>
                </Alert>
              )}
              
              <HStack spacing={4}>
                <Button
                  colorScheme="purple"
                  leftIcon={<MdUploadFile />}
                  onClick={handleImport}
                  isLoading={importing}
                  loadingText="Importando..."
                  isDisabled={!selectedFile || !selectedState}
                  size="lg"
                >
                  Importar
                </Button>
                
                {(selectedFile || importResults) && (
                  <Button
                    variant="outline"
                    onClick={handleReset}
                    isDisabled={importing}
                  >
                    Limpar
                  </Button>
                )}
              </HStack>
            </VStack>
          </CardBody>
        </Card>
        
        {/* Import Results */}
        {importResults && (
          <Card>
            <CardBody>
              <VStack align="stretch" spacing={4}>
                <Heading size="md">Resultados da Importação</Heading>
                
                <StatGroup>
                  <Stat>
                    <StatLabel>Total de Linhas</StatLabel>
                    <StatNumber>{importResults.summary.totalRows}</StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel color="green.500">Atualizados</StatLabel>
                    <StatNumber color="green.500">
                      <Icon as={MdCheckCircle} mr={1} />
                      {importResults.summary.successfulUpdates}
                    </StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel color="orange.500">Não Encontrados</StatLabel>
                    <StatNumber color="orange.500">
                      <Icon as={MdWarning} mr={1} />
                      {importResults.summary.recordsNotFound}
                    </StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel color="red.500">Erros</StatLabel>
                    <StatNumber color="red.500">
                      <Icon as={MdError} mr={1} />
                      {importResults.summary.errors}
                    </StatNumber>
                  </Stat>
                </StatGroup>
                
                <Divider />
                
                {/* Additional Statistics */}
                {importResults.summary && (
                  <>
                    <StatGroup>
                      <Stat>
                        <StatLabel>Emails Adicionados</StatLabel>
                        <StatNumber color="blue.500">
                          <Icon as={MdEmail} mr={1} />
                          {importResults.summary.emailsAdded || 0}
                        </StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel>Telefones Adicionados</StatLabel>
                        <StatNumber color="purple.500">
                          {importResults.summary.phonesAdded || 0}
                        </StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel>CPFs Adicionados</StatLabel>
                        <StatNumber color="teal.500">
                          {importResults.summary.cpfsAdded || 0}
                        </StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel>Cidades Adicionadas</StatLabel>
                        <StatNumber color="cyan.500">
                          {importResults.summary.cidadesAdded || 0}
                        </StatNumber>
                      </Stat>
                    </StatGroup>
                    
                    <Divider />
                    
                    {/* Detailed breakdown */}
                    <Box>
                      <Text fontSize="sm" fontWeight="bold" mb={2}>Detalhamento da Importação:</Text>
                      {importResults.summary.oabsThatReceivedEmails > 0 && (
                        <Alert status="success" size="sm" mb={2}>
                          <AlertIcon />
                          <Text fontSize="sm">
                            <Text as="span" fontWeight="bold">{importResults.summary.oabsThatReceivedEmails}</Text> OABs receberam emails nesta importação
                          </Text>
                        </Alert>
                      )}
                      {importResults.summary.oabsNewlyWithEmails > 0 && (
                        <Alert status="success" size="sm" mb={2}>
                          <AlertIcon />
                          <Text fontSize="sm">
                            <Text as="span" fontWeight="bold">{importResults.summary.oabsNewlyWithEmails}</Text> OABs receberam seu primeiro email
                          </Text>
                        </Alert>
                      )}
                      {importResults.summary.oabsAlreadyWithEmailsUpdated > 0 && (
                        <Alert status="info" size="sm" mb={2}>
                          <AlertIcon />
                          <Text fontSize="sm">
                            <Text as="span" fontWeight="bold">{importResults.summary.oabsAlreadyWithEmailsUpdated}</Text> OABs que já tinham email receberam dados adicionais
                          </Text>
                        </Alert>
                      )}
                      {importResults.summary.oabsNewlyWithPhones > 0 && (
                        <Alert status="success" size="sm">
                          <AlertIcon />
                          <Text fontSize="sm">
                            <Text as="span" fontWeight="bold">{importResults.summary.oabsNewlyWithPhones}</Text> OABs receberam seu primeiro telefone
                          </Text>
                        </Alert>
                      )}
                    </Box>
                  </>
                )}
                
                {/* Progress Bar if importing */}
                {importProgress && importProgress.status === 'processing' && (
                  <Box>
                    <Text fontSize="sm" mb={2}>
                      Processando... {importProgress.processedRows} de {importProgress.totalRows} linhas
                    </Text>
                    <Progress 
                      value={importProgress.percentComplete} 
                      size="sm" 
                      colorScheme="purple" 
                      isAnimated 
                      hasStripe 
                    />
                  </Box>
                )}
              </VStack>
            </CardBody>
          </Card>
        )}
      </VStack>
    </Box>
  );
};

export default OABEmailImport;